import dayjs from 'dayjs'
import { Configs } from 'constants/configs'
import { IExerciseRecords } from 'hooks/useExerciseRecords'
import { FC, ReactNode, RefObject, useState } from 'react'
import { LuChevronDown, LuUser } from 'react-icons/lu'
import { RangeBadge } from 'components/ui/Section'
import { EmptyState, Skeleton, Spinner } from 'components/ui/States'
import { formatClock, formatInt, getStudentLabel } from 'utils/stats'
import { getExerciseName, getModeName } from 'variables/excersise'

interface IRecordsTable {
    records: IExerciseRecords[]
    total: number
    range: string
    scrollRef: RefObject<HTMLDivElement>
    onScroll: () => void
    isInitialLoading: boolean
    isFetchingNextPage: boolean
    hasNextPage: boolean
    emptyAction?: ReactNode
}

/** Neutral pill (DESIGN.md §4.7): status colours are reserved for status and deltas. */
const ModePill: FC<{ mode: string }> = ({ mode }) => (
    <span className='pill whitespace-nowrap bg-canvas-deep text-ink-muted'>{getModeName(mode)}</span>
)

const Avatar: FC<{ record: IExerciseRecords }> = ({ record }) => record.icon
    ? <img src={`${Configs.baseUrl}${record.icon.url}`} alt='' loading='lazy' className='h-9 w-9 shrink-0 rounded-full bg-canvas-deep object-cover' />
    : <span className='grid h-9 w-9 shrink-0 place-items-center rounded-full bg-canvas-deep text-ink-faint'><LuUser aria-hidden className='h-4 w-4' /></span>

const formatRecordDate = (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm')

/** Section J: all records with infinite scroll (table ≥ sm, stacked cards on phones). */
export const RecordsTable: FC<IRecordsTable> = ({ records, total, range, scrollRef, onScroll, isInitialLoading, isFetchingNextPage, hasNextPage, emptyAction }) => {
    const [isOpen, setIsOpen] = useState(false)
    const isEmpty = !isInitialLoading && records.length === 0

    return <section className='card min-w-0 overflow-hidden'>
        <div className={`flex flex-wrap items-start justify-between gap-3 px-4 py-4 sm:px-6 ${isOpen ? 'border-b border-line-soft' : ''}`}>
            <div>
                <h3 className='text-h3 text-ink'>所有運動紀錄</h3>
                <p className='mt-0.5 text-sm text-ink-faint'>每一次運動的詳細紀錄，向下捲動載入更多</p>
            </div>
            <div className='flex flex-wrap items-center gap-2'>
                <RangeBadge range={range} />
                <span className='pill bg-canvas-deep text-ink tabular-nums'>總次數: {formatInt(total)}</span>
                <button
                    type='button'
                    className='btn btn-secondary'
                    aria-expanded={isOpen}
                    aria-controls='records-table-body'
                    data-print-hide
                    onClick={() => setIsOpen((open) => !open)}>
                    {isOpen ? '收起紀錄' : '展開紀錄'}
                    <LuChevronDown aria-hidden className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
            </div>
        </div>

        {!isOpen ? null : isEmpty
            ? <EmptyState hint='試試調整日期範圍' action={emptyAction} />
            : <div id='records-table-body' ref={scrollRef} onScroll={onScroll} className='max-h-[560px] overflow-auto overscroll-contain'>
                {/* Phone: stacked cards */}
                <ul className='divide-y divide-line-soft sm:hidden'>
                    {isInitialLoading && Array.from({ length: 6 }).map((_, i) => <li key={i} className='space-y-2 p-4'>
                        <Skeleton className='h-4 w-1/2' /><Skeleton className='h-4 w-3/4' />
                    </li>)}
                    {records.map((item) => <li key={item.id} className='flex gap-3 p-4'>
                        <Avatar record={item} />
                        <div className='min-w-0 flex-1'>
                            <div className='flex items-start justify-between gap-2'>
                                <p className='truncate font-medium text-ink'>{getExerciseName(item.exercise)}</p>
                                <ModePill mode={item.mode} />
                            </div>
                            <p className='mt-0.5 truncate text-sm text-ink-muted'>{getStudentLabel(item)}</p>
                            <div className='mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-faint tabular-nums'>
                                <span>{formatRecordDate(item.date)}</span>
                                <span>完成 <span className='font-semibold text-ink'>{formatInt(item.count)}</span> 下</span>
                                <span title={`${formatInt(item.durationInSecond)} 秒`}>時間 <span className='font-semibold text-ink'>{formatClock(item.durationInSecond)}</span></span>
                                <span>位置 {item.position ?? '—'}</span>
                            </div>
                        </div>
                    </li>)}
                </ul>

                {/* ≥ sm: table */}
                <table className='hidden w-full min-w-[820px] text-left text-sm sm:table'>
                    <thead className='sticky top-0 z-10 bg-canvas-deep text-xs font-semibold text-ink-muted'>
                        <tr>
                            <th scope='col' className='whitespace-nowrap px-4 py-3 sm:pl-6'>ID</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3'>日期</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3'>參加者</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3'>項目</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3 text-right'>完成次數</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3'>模式</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3 text-right'>參加位置</th>
                            <th scope='col' className='whitespace-nowrap px-4 py-3 text-right sm:pr-6'>運動時間</th>
                        </tr>
                    </thead>
                    <tbody className='divide-y divide-line-soft'>
                        {isInitialLoading && Array.from({ length: 6 }).map((_, i) => <tr key={i}>
                            {Array.from({ length: 8 }).map((__, j) => <td key={j} className='px-4 py-4'><Skeleton className='h-4 w-full' /></td>)}
                        </tr>)}
                        {records.map((item) => <tr key={item.id} className='transition-colors hover:bg-canvas'>
                            <td className='px-4 py-3 text-ink-faint tabular-nums sm:pl-6'>{item.id}</td>
                            <td className='whitespace-nowrap px-4 py-3 text-ink tabular-nums'>{formatRecordDate(item.date)}</td>
                            <td className='px-4 py-3'>
                                <span className='flex items-center gap-3'>
                                    <Avatar record={item} />
                                    <span className='whitespace-nowrap text-ink'>{getStudentLabel(item)}</span>
                                </span>
                            </td>
                            <td className='whitespace-nowrap px-4 py-3 font-medium text-ink'>{getExerciseName(item.exercise)}</td>
                            <td className='px-4 py-3 text-right font-medium tabular-nums'>{formatInt(item.count)}</td>
                            <td className='px-4 py-3'><ModePill mode={item.mode} /></td>
                            <td className='px-4 py-3 text-right tabular-nums'>{item.position ?? '—'}</td>
                            <td className='px-4 py-3 text-right tabular-nums sm:pr-6' title={`${formatInt(item.durationInSecond)} 秒`}>{formatClock(item.durationInSecond)}</td>
                        </tr>)}
                    </tbody>
                </table>

                {!isInitialLoading && <div className='flex items-center justify-center gap-2 border-t border-line-soft py-4 text-center text-sm text-ink-faint'>
                    {isFetchingNextPage
                        ? <><Spinner />載入更多…</>
                        : hasNextPage
                            ? `已載入 ${formatInt(records.length)} / ${formatInt(total)} 筆，向下捲動載入更多`
                            : `已顯示全部 ${formatInt(total)} 筆`}
                </div>}
            </div>}
    </section>
}
